import React, { useCallback, useContext, useEffect } from 'react';
import { Redirect, Switch } from 'react-router';
import { Link } from 'react-router-dom';
import { Tab, useTabs, validatePageNumber, validateSorting } from 'src/client/helpers';
import { MoviesContent, MoviesContentProps } from './MoviesContent';
import { getMovieSortTypes, moviesRoutes, routes } from '../../utils/router';
import CountTab from '../CountTabs/CountTab';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import CamsSlider from '../Cams/CamsSlider/CamsSlider';
import { camsSliderSettings } from 'src/client/helpers/constants';
import { CamsContext } from 'src/client/containers/Cams/CamsContextProvider';
import { CamsTitle } from 'src/client/containers/Cams/CamsSection/CamsTitle';
import { GuardedRoute } from 'src/client/utils/GuardedRoute';
import { MetartIntimateMovies } from './MetartIntimateMovies';
import { StaffSelection } from './StaffSelection';
import { PPSMovies } from './PPSMovies';
import { FanFictionMovies } from './FanFictionMovies';
import { SortType } from 'src/shared/constants/sortType';
import { AppConfig } from 'src/client/redux/modules/app';
import { OnPageText } from 'src/client/components/OnPageSeo/OnPageText/OnPageText';
import { IconOnlySortAndViewDropdown } from 'src/client/components/dropdowns';
import { SORT_TYPES as SORT_TYPES_MOVIES } from 'src/shared/constants/movies';
import styled from 'styled-components';
import { ContentHeader } from 'src/client/components/ui/ContentHeader';
import CountTabs from '../CountTabs/CountTabs';
import { ConstrainedContainer, MOBILE_LANDSCAPE, WidePageWrapper } from 'src/client/constants/mediaQueries';
import { useWidePageContext } from '../App/WidePageContextProvider';
import { MainPageTitle } from 'src/client/components/layouts/MainPageTitle';

const {
  movies: {
    routes: {
      staffSelection: staffSelectionRoute,
      fanFiction: fanFictionRoute,
      myMovies: myMoviesRoute,
      movies: moviesRoute,
      metartIntimate: intimateRoute,
    },
  },
} = routes;

const sortKeyByTab = {
  Movies: 'filmsSorting',
  'MetArt Intimate': 'filmsSorting',
};

const sortTypes = {
  Movies: SORT_TYPES_MOVIES,
  'MetArt Intimate': SORT_TYPES_MOVIES,
};

export default function Movies() {
  const itemCounts = useSelector((state) => state.itemCounts);
  const { user } = useSelector((state) => state.auth);
  const config = useSelector((state) => state.app.config);
  const cams = useContext(CamsContext);

  useEffect(() => {
    document.body.classList.add('show-jsd'); // show jsd plugin
    return () => document.body.classList.remove('show-jsd');
  }, []);

  const translatedMovieTabs: Array<Tab> | undefined = config.moviesTabs?.map((tab) => ({
    ...tab,
    value: tab.value,
    name: tab.label,
    to: tab.link,
    headerTitle: tab.label,
  }));

  const movieTabs: Array<Tab> = [
    {
      headerTitle: 'Erotic Films',
      name: 'Erotic Films',
      to: '/erotic-films',
      value: 'Movies',
    },
    {
      disabled: config.disableMetartIntimate,
      headerTitle: <span>MetArt Intimate</span>,
      name: 'MetArt Intimate',
      showCamsOnTab: true,
      to: '/erotic-films/intimate',
      value: 'MetArt Intimate',
    },
    {
      disabled: !config.hasStaffSelection,
      headerTitle: <span>Staff Selections</span>,
      name: 'Staff Selections',
      requiresAuth: true,
      to: '/erotic-films/staff',
      value: 'Staff Selection',
    },
    {
      headerTitle: <span>My Movies</span>,
      name: 'My Movies',
      requiresAuth: true,
      requiresPPSMovies: true,
      to: '/erotic-films/my',
      value: 'PPSMovies',
    },
  ];

  const [tabs, activeTab] = useTabs('movies', translatedMovieTabs || movieTabs, moviesRoutes);
  const showCamsTitle = !config.hasUpdates && cams.loaded;

  const movieSortTypes = getMovieSortTypes(config);
  const defaultSort = movieSortTypes[0].id;

  const widePage = useWidePageContext();

  const renderContentHeader = useCallback(
    ({ collapsePadding = false, showViewOptionsInTabs = false } = {}) => {
      if (!activeTab) {
        return null;
      }

      const tabComponents = tabs
        .filter((countTab) => {
          if (countTab.requiresPPSMovies) {
            return itemCounts && itemCounts.PPSMovies && itemCounts.PPSMovies.total > 0;
          }
          return true;
        })
        .map((tab) => <CountTab key={tab.value} active={tab.value === activeTab?.value} as={Link} {...tab} />);

      if (showViewOptionsInTabs) {
        tabComponents.push(
          <IconOnlySortAndViewDropdown
            key="options"
            sortSettingsKey={sortKeyByTab[activeTab.value]}
            sortTypes={sortTypes[activeTab.value]}
            pageType="movies"
            tab={activeTab.value}
          />,
        );
      }

      const tabsNode = <StyledTabs countParams={[...tabs.map((tab) => tab.value)]}>{tabComponents}</StyledTabs>;

      return (
        <StyledContentHeader
          collapsePadding={collapsePadding}
          tabs={tabsNode}
          title={null}
          subTitle={<OnPageText contentName="text1" />}
        />
      );
    },
    [activeTab, tabs, itemCounts],
  );
  return (
    <MoviesContainerSwitcher>
      {!config.hasUpdates && user && (
        <CamsSlider camTrackingPosition="sliderTop" mobileOnly settings={camsSliderSettings} />
      )}
      <MainPageTitle>{activeTab?.headerTitle}</MainPageTitle>
      {renderContentHeader({ collapsePadding: widePage, showViewOptionsInTabs: widePage })}

      {showCamsTitle ? <CamsTitle camTrackingPosition="sideBar" /> : null}
      <Switch>
        <GuardedRoute
          requiresAuth
          exact
          path={staffSelectionRoute.path}
          component={StaffSelection}
          redirectLocation="/erotic-films/staff"
          validationFn={pageNumberGuard}
          disabled={!config.hasStaffSelection}
          disabledRedirectLocation="/erotic-films"
        />

        <GuardedRoute
          requiresAuth
          exact
          path={myMoviesRoute.path}
          validationFn={pageNumberGuard}
          component={PPSMovies}
          redirectLocation={`/erotic-films/my/${defaultSort}`}
        />

        <GuardedRoute
          exact
          path={intimateRoute.path}
          component={MetartIntimateMovies}
          validationFn={pageNumberGuard}
          redirectLocation="/erotic-films/intimate"
          disabled={config.disableMetartIntimate}
          disabledRedirectLocation="/erotic-films"
        />

        <GuardedRoute
          exact
          path={fanFictionRoute.path}
          component={FanFictionMovies}
          redirectLocation="/erotic-films/fan-fiction"
          validationFn={fanFictionGuard(movieSortTypes)}
          disabledRedirectLocation="/erotic-films"
        />

        <GuardedRoute
          exact
          path={moviesRoute.path}
          component={MoviesContent}
          redirectLocation={`/erotic-films/${config.movies.redirectTab}`}
          validationFn={moviesContentGuard(movieSortTypes, config)}
        />

        <Redirect exact from="/erotic-films" to={`/erotic-films/${config.movies.redirectTab || defaultSort}`} />

        <Redirect to={`/erotic-films/${config.movies.redirectTab || defaultSort}`} />
      </Switch>
    </MoviesContainerSwitcher>
  );
}

const pageNumberGuard = ({ match }: MoviesContentProps) => validatePageNumber(match.params.page);

const fanFictionGuard = (movieSortTypes: SortType[]) => {
  return ({ match }) => {
    if (!match.params.sortBy) return true;

    if (
      match.params.sortBy &&
      validateSorting(match.params.sortBy, movieSortTypes) &&
      validatePageNumber(match.params.page)
    )
      return true;

    return false;
  };
};

function moviesContentGuard(movieSortTypes: SortType[], config: Partial<AppConfig>) {
  return ({ match }) => {
    if (
      match.params.sortBy &&
      validateSorting(match.params.sortBy, movieSortTypes) &&
      validatePageNumber(match.params.page)
    )
      return true;

    if (config.movies.redirectTab) return false;

    if (validatePageNumber(match.params.sortBy)) return true;

    return true;
  };
}

// @TODO
// Remove after all pages are converted to wide mode.
const MoviesContainerSwitcher: React.FC = ({ children }) => {
  const widePage = useWidePageContext();

  if (widePage) {
    return (
      <WidePageWrapper>
        <Div as={ConstrainedContainer} extended>
          {children}
        </Div>
      </WidePageWrapper>
    );
  }

  return <Div className="content container">{children}</Div>;
};

const Div = styled.div<{ extended?: boolean }>`
  .options {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .sort-dropdown {
      margin-right: 20px;
    }
  }
`;

const StyledContentHeader = styled(ContentHeader)<{ collapsePadding?: boolean }>`
  padding-top: ${(props) => (props.collapsePadding ? '0' : null)};
`;

const StyledTabs = styled(CountTabs)`
  display: flex;
  padding-right: 5px;
  align-items: center;

  .tab {
    padding: 10px 15px;
    max-width: 130px;
    @media ${MOBILE_LANDSCAPE} {
      max-width: none;
    }
  }

  .tab span {
    display: block;
    @media ${MOBILE_LANDSCAPE} {
      display: inline;
    }
  }
`;
